/**
 * Every mutation the Card Editor can make, as a plain data action.
 *
 * The set is deliberately intent-shaped rather than generic ("pair this block
 * with that one", not "write these placements") so the future API can
 * re-enforce the same rules server-side without reverse-engineering a blob
 * (threat model, abuse case 3).
 */

import {
  CardOrientation,
  CtaConfiguration,
  DescriptionStyle,
  FieldAlignment,
  FieldId,
  ImageFit,
  PhotoAnchor,
  ProductCard,
} from '../types';

export type PairSide = 'left' | 'right';

export type CardAction =
  /** Replace the whole document — used when the editor loads a card. */
  | { type: 'loadCard'; card: ProductCard }
  | { type: 'rename'; name: string }
  | { type: 'setOrientation'; orientation: CardOrientation }
  | { type: 'setFooterEnabled'; enabled: boolean }
  | { type: 'toggleField'; fieldId: FieldId; enabled: boolean }
  /** Rail reorder: `beforeFieldId` of null sends the field to the end. */
  | { type: 'moveField'; fieldId: FieldId; beforeFieldId: FieldId | null }
  | { type: 'setDisplayLabel'; fieldId: FieldId; displayLabel: string }
  | { type: 'setAlignment'; fieldId: FieldId; alignment: FieldAlignment }
  /**
   * How the Primary Image fills the photo region. It carries no `fieldId`: the
   * image is the only field with this setting, so the action cannot name the
   * wrong one.
   */
  | { type: 'setImageFit'; imageFit: ImageFit }
  /** The element's own CSS declarations, as the user typed them (spec R67). */
  | { type: 'setCustomCss'; fieldId: FieldId; customCss: string }
  | { type: 'setStrikethrough'; fieldId: FieldId; strikethrough: boolean }
  /**
   * Append the unit of measure to every price on the card (spec R69). Card-wide,
   * so it carries no `fieldId` — all four Pricing panels write this one flag.
   */
  | { type: 'setShowUom'; fieldId: FieldId; showUom: boolean }
  /**
   * How the Short Description renders (spec R71). No `fieldId`, for the same reason
   * `setImageFit` has none: only one field has this setting.
   */
  | { type: 'setDescriptionStyle'; descStyle: DescriptionStyle }
  /**
   * One or more of the Primary CTA Button's options (spec R70). A patch rather than
   * the whole group: each control in the Button section changes exactly the option
   * it is named after, and the reducer fills the rest from what is already stored.
   */
  | { type: 'setCtaOptions'; options: Partial<CtaConfiguration> }
  /** Drop into Info as its own row at `rowIndex` (spec R50). */
  | { type: 'moveBlockToInfoRow'; fieldId: FieldId; rowIndex: number }
  /** Drop beside an existing Info block, pairing them in one row (spec R51). */
  | { type: 'pairBlock'; fieldId: FieldId; peerFieldId: FieldId; side: PairSide }
  /** Drop onto one of the six photo anchors (spec R52). */
  | { type: 'moveBlockToPhoto'; fieldId: FieldId; anchor: PhotoAnchor }
  /** Drop into the footer strip (spec R53). */
  | { type: 'moveBlockToFloating'; fieldId: FieldId }
  /** Remove the block and disable its field (spec R57). */
  | { type: 'deleteBlock'; fieldId: FieldId };

export const cardActions = {
  loadCard: (card: ProductCard): CardAction => ({ type: 'loadCard', card }),
  rename: (name: string): CardAction => ({ type: 'rename', name }),
  setOrientation: (orientation: CardOrientation): CardAction => ({
    type: 'setOrientation',
    orientation,
  }),
  setFooterEnabled: (enabled: boolean): CardAction => ({
    type: 'setFooterEnabled',
    enabled,
  }),
  toggleField: (fieldId: FieldId, enabled: boolean): CardAction => ({
    type: 'toggleField',
    fieldId,
    enabled,
  }),
  moveField: (fieldId: FieldId, beforeFieldId: FieldId | null): CardAction => ({
    type: 'moveField',
    fieldId,
    beforeFieldId,
  }),
  setDisplayLabel: (fieldId: FieldId, displayLabel: string): CardAction => ({
    type: 'setDisplayLabel',
    fieldId,
    displayLabel,
  }),
  setAlignment: (fieldId: FieldId, alignment: FieldAlignment): CardAction => ({
    type: 'setAlignment',
    fieldId,
    alignment,
  }),
  setImageFit: (imageFit: ImageFit): CardAction => ({ type: 'setImageFit', imageFit }),
  /** Per element, unlike setShowUom — it carries the field id (14021). */
  setStrikethrough: (fieldId: FieldId, strikethrough: boolean): CardAction => ({
    type: 'setStrikethrough',
    fieldId,
    strikethrough,
  }),

  setCustomCss: (fieldId: FieldId, customCss: string): CardAction => ({
    type: 'setCustomCss',
    fieldId,
    customCss,
  }),
  /** Per element since 14021 — it used to be card-wide and carry no field id. */
  setShowUom: (fieldId: FieldId, showUom: boolean): CardAction => ({
    type: 'setShowUom',
    fieldId,
    showUom,
  }),
  setDescriptionStyle: (descStyle: DescriptionStyle): CardAction => ({
    type: 'setDescriptionStyle',
    descStyle,
  }),
  setCtaOptions: (options: Partial<CtaConfiguration>): CardAction => ({
    type: 'setCtaOptions',
    options,
  }),
  moveBlockToInfoRow: (fieldId: FieldId, rowIndex: number): CardAction => ({
    type: 'moveBlockToInfoRow',
    fieldId,
    rowIndex,
  }),
  pairBlock: (fieldId: FieldId, peerFieldId: FieldId, side: PairSide): CardAction => ({
    type: 'pairBlock',
    fieldId,
    peerFieldId,
    side,
  }),
  moveBlockToPhoto: (fieldId: FieldId, anchor: PhotoAnchor): CardAction => ({
    type: 'moveBlockToPhoto',
    fieldId,
    anchor,
  }),
  moveBlockToFloating: (fieldId: FieldId): CardAction => ({
    type: 'moveBlockToFloating',
    fieldId,
  }),
  deleteBlock: (fieldId: FieldId): CardAction => ({ type: 'deleteBlock', fieldId }),
};
